/**
 * AdminEditPoolModal (T8.3) — edit `max_tenants` only.
 *
 * R2 deferral: host/port/dbName/status fields → F15. Tu wyłącznie maxTenants.
 *
 * Backend: PATCH /admin/db-pools/:id { maxTenants }
 * Errors:
 *   - MAX_TENANTS_BELOW_CURRENT (400) → tłumaczymy na inline error
 *   - FIELD_DEFERRED_TO_F15 (400) → niemożliwe z tego modala (wysyłamy tylko maxTenants)
 */

import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { api } from '@/lib/api';
import type { DbPoolListItem } from '@/pages/AdminDbPools';

export interface AdminEditPoolModalProps {
  pool: DbPoolListItem;
  onClose: () => void;
  onSuccess: () => void;
}

export function AdminEditPoolModal({ pool, onClose, onSuccess }: AdminEditPoolModalProps) {
  const { t } = useTranslation();
  const [maxTenants, setMaxTenants] = useState<number>(pool.maxTenants);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formValid =
    Number.isInteger(maxTenants) &&
    maxTenants >= 1 &&
    maxTenants <= 1000 &&
    maxTenants >= pool.currentTenantCount;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formValid) return;
    setError(null);
    setSubmitting(true);
    try {
      await api.patch(`/admin/db-pools/${pool.id}`, { maxTenants });
      onSuccess();
    } catch (err) {
      const axErr = err as AxiosError<{ error?: string; details?: Record<string, unknown> }>;
      const errorCode = axErr.response?.data?.error ?? null;
      if (errorCode === 'MAX_TENANTS_BELOW_CURRENT') {
        setError(t('admin.dbPools.modal.errors.belowCurrent'));
      } else {
        setError(t('admin.dbPools.modal.errors.generic'));
      }
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => (submitting ? undefined : onClose())}
      title={t('admin.dbPools.modal.titleEdit', { name: pool.name })}
      closeOnBackdrop={!submitting}
      closeOnEsc={!submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('admin.dbPools.modal.cancel')}
          </Button>
          <Button
            type="submit"
            form="db-pool-form"
            variant="primary"
            loading={submitting}
            disabled={submitting || !formValid}
            data-testid="edit-pool-submit"
          >
            {t('admin.dbPools.modal.confirm')}
          </Button>
        </>
      }
    >
      <form id="db-pool-form" onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <div className="rounded-lg border border-border-primary bg-bg-secondary px-3 py-2 text-xs text-text-tertiary">
          <p>
            <span className="font-medium">{pool.name}</span> · {pool.dbHost ?? '—'}
          </p>
          <p className="mt-1">
            {t('admin.dbPools.modal.currentTenants', { count: pool.currentTenantCount })}
          </p>
        </div>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-text-secondary">
            {t('admin.dbPools.modal.maxTenantsLabel')}
          </span>
          <input
            type="number"
            min={Math.max(1, pool.currentTenantCount)}
            max={1000}
            step={1}
            value={maxTenants}
            onChange={(e) => setMaxTenants(parseInt(e.target.value, 10) || 0)}
            disabled={submitting}
            className="rounded-lg border border-border-primary bg-bg-secondary px-3 py-2 text-sm text-text-primary disabled:opacity-50"
            data-testid="edit-pool-max-tenants"
          />
          {!formValid && maxTenants < pool.currentTenantCount && (
            <span className="text-xs text-red-600">
              {t('admin.dbPools.modal.belowCurrentInline', {
                count: pool.currentTenantCount,
              })}
            </span>
          )}
        </label>

        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
          >
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
